import { Ingredient } from '@cf-shared/data-model/ingredient';
import { NutrientTargets } from '@cf-shared/data-model/food';
import { Log_Meal } from '@cf-shared/data-model/log-meal';
import { PageRef } from 'providers/page-ref';

export enum EditIngredientMode {
  New = 'new',
  Edit = 'edit',
  Log = 'log',
}

/**
 * How many ingredients the caller that opened this screen is able to apply from its answer.
 *
 * This screen can hand back MORE than the one food it was opened on: the two Split buttons and
 * the More menu's Split item break a recipe into its separate foods and pop with
 * `action: 'split'` and a whole list. That only works when the caller has somewhere to put a
 * list. A caller that can hold only one food must say so, or the user is shown controls whose
 * answer is then thrown away. Deferred finding df-d3f1a6b577ee was exactly that: pressing Split
 * on a recipe the meal-photo scanner had matched did nothing at all, with nothing reported
 * anywhere.
 *
 * Absent means `SplitOrSingleIngredient`, which is what every caller but the meal-photo scanner
 * wants.
 */
export enum EditIngredientReturnCapability {
  /** The caller can apply a split, so this screen offers its Split controls. */
  SplitOrSingleIngredient = 'splitOrSingleIngredient',
  /** The caller can apply only ONE ingredient, so this screen offers no way to split. */
  SingleIngredientOnly = 'singleIngredientOnly',
}

export interface EditIngredientPageParams {
  ingredientIndex: number; // -1 for new ingredients
  mode: EditIngredientMode;
  /**
   * What the caller can apply from this screen's answer. See
   * `EditIngredientReturnCapability`. Omit it unless the caller can hold only one ingredient.
   */
  returnCapability?: EditIngredientReturnCapability;
  // Log mode parameters:
  dayDate?: string; // ISO date string for logging
  selectedMealId?: string; // pre-selected meal ID
  foodId?: string; // food ID to create ingredient from (Log mode)
  logMealId?: string; // log meal ID to copy ingredients from (Log mode)
  // Callback parameters:
  returnCallback?: string;
  // The parent's EARLY handler, run before this page's closing transition instead of after it.
  // A page only declares this when something needs it; the timings and the rule that such a
  // handler must not navigate are on `CsPage` in `providers/nav-controller.ts`.
  returnCallbackImmediate?: string;
  // Complex objects stored in sessionStorage, not URL
  local?: {
    ingredients: Ingredient[];
    /**
     * The meal's OWN plan targets — `Log_Meal.targets`, and never the stored
     * `Log_Meal.planningTargets`. This is what the meal scope's "Impact to Meal Targets"
     * card measures the running total against.
     *
     * The distinction is the whole point of `design-docs/meal-target-figures.md`, and it
     * cost this screen a real defect (#5297). `planningTargets` answers "what should still
     * go into this slot", and it is computed from a day total that already includes this
     * meal's own food — so passing it here graded a meal against a target its own contents
     * had made smaller. It is exactly right while a slot is empty and wrong the moment it
     * is not.
     *
     * `meal.targets` is also what every other surface that grades a meal uses: the meal
     * editor's own macro card, the Daily Log's per-meal match, and this screen itself when
     * it is reached through its own meal picker (`selectedLogMeal.targets`). Passing
     * anything else here makes one screen disagree with the screen behind it.
     *
     * Absent when there is no meal behind this screen, and empty on a meal the user added
     * outside their plan; either way the meal scope is simply not offered.
     */
    targets?: NutrientTargets;
    /**
     * The day these ingredients belong to, as an ISO date, and the meal that holds
     * them. Together they are what lets the macro card offer its `Day` scope on a
     * screen opened from a meal rather than from the day's food list. This is the
     * canonical account of the three; everything that passes them points here.
     *
     * **All three or none.** Each earns its place:
     *
     * - the DATE is what the day is read for;
     * - the ID is what the day's sum leaves out, so the meal being edited is not
     *   counted twice -- once from its stored figures and again from the amount
     *   being typed. On this path there is no meal picker and so no
     *   `selectedLogMeal` to name it;
     * - the NAME decides which SLOT the meal occupies, and two meals sharing a name
     *   are two documents for one slot. It is needed beside the id because a meal
     *   being built is not saved anywhere yet, so the id alone cannot be looked up
     *   to find the name.
     *
     * Absent on a screen with no day behind it at all, such as a recipe, where the
     * scope dial simply offers one position fewer.
     */
    dayDate?: string;
    mealId?: string;
    mealName?: string;
    pendingIngredient?: Ingredient; // for new mode - ingredient not yet in list
    sourceLogMeal?: Log_Meal; // for new mode - log meal to preview before adding its ingredients
    returnContext?: unknown;
  };
}

export interface EditIngredientResult {
  action: 'save' | 'delete' | 'split';
  ingredient?: Ingredient;
  splitIngredients?: Ingredient[];
  basedOnFoodId?: string;
  tunedExistingIngredients?: Ingredient[]; // existing ingredients with tuned weights (for combined auto-tune)
  /**
   * How much was logged, already worded for reading — "2 large", or "100 g" (#4967).
   *
   * The portion screen works this out anyway, and it is the only place that holds what it
   * needs: the food's own measures and the unit the user was looking at. A caller that shows
   * the "food added" confirmation reads it from here rather than recomputing it, so the two
   * places that show that message cannot disagree about the amount.
   */
  loggedAmountDescription?: string;
}

export const IEditIngredientPage: PageRef<EditIngredientPageParams, EditIngredientResult> = {
  pageName: 'EditIngredientPage',
} as PageRef<EditIngredientPageParams, EditIngredientResult>;
